import { Button } from '@components/utility/buttons';
import { GradientChip } from '@components/utility/chips';
import { Box, BoxProps, Divider, List, ListItem, Stack, Typography } from '@mui/material';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

type Feature = {
    icon: React.ReactNode;
    title: React.ReactNode;
    description: React.ReactNode;
};

type Props = Omit<BoxProps, 'title'> & {
    icon: React.ReactNode;
    title: React.ReactNode;
    features: Feature[];
    linkTo: string;
};

export const ApiCard = ({ title, icon, features, linkTo, ...boxProps }: Props) => {
    return (
        <Box
            component="article"
            borderRadius="6px"
            bgcolor="background.paper"
            display="flex"
            flexDirection="column"
            {...boxProps}
            className={clsx('black-border', boxProps.className)}
        >
            <List className="lg:pb-[48px]">
                <Stack component={ListItem} direction="row" spacing="12px" alignItems="start">
                    <Box bgcolor="primary-100.main" p="4px" borderRadius="6px" width="fit-content">
                        {icon}
                    </Box>
                    <Typography fontFamily="museo-sans" fontSize={18} letterSpacing={-0.36}>
                        {title}
                    </Typography>
                    <GradientChip
                        className="ml-auto"
                        label={<FormattedMessage id="developers.api.rest" defaultMessage="REST API" />}
                    />
                </Stack>
                <Divider component="li" variant="middle" className="mb-[16px]" />
                {features.map((feature, index) => (
                    <>
                        <Stack
                            key={index}
                            component={ListItem}
                            direction="row"
                            spacing="12px"
                            alignItems="start"
                            py="10px"
                        >
                            <Box
                                bgcolor="neutral-100.main"
                                p="8px"
                                border="1px solid rgba(50, 2, 2, 0.12)"
                                borderRadius="6px"
                                width="fit-content"
                            >
                                {feature.icon}
                            </Box>
                            <Box>
                                <Typography fontFamily="museo-sans" fontSize={13} fontWeight={600}>
                                    {feature.title}
                                </Typography>
                                <Typography fontSize={11.5} color="neutral-800" fontWeight={500}>
                                    {feature.description}
                                </Typography>
                            </Box>
                        </Stack>
                        {index < features.length - 1 && <Divider component="li" variant="middle" />}
                    </>
                ))}
            </List>
            <Box px="16px" pb="16px" className="mt-auto">
                <Button fullRounded fullWidth {...{ component: Link, to: linkTo }}>
                    <FormattedMessage id="common.learnMore" defaultMessage="Learn more" />
                </Button>
            </Box>
        </Box>
    );
};
